/* ==========================================================================
   Melanated Princess — light 3D layer for the homepage
   --------------------------------------------------------------------------
   Writes the custom properties that assets/mp-depth.css reads. Four jobs:

     [data-mp-parallax]  hero: pointer position (--px/--py) and how far it has
                         scrolled away (--sy)
     [data-mp-slider]    hero: slides, progress lines, arrows, swipe, autoplay
     [data-mp-tilt]      card: tilt angle (--rx/--ry) and glare spot (--gx/--gy)
     [data-mp-flip]      button: turns a product card around on touch screens
     [data-mp-switcher]  Radiant and book sections: a row of [data-mp-swatch]
                         buttons swaps the matching [data-mp-shot] picture,
                         [data-mp-detail] panel and background colour
                         (optional data-autoplay seconds rotates it)
     [data-mp-counters]  numbers in [data-count] count up when seen
     [data-mp-bundle]    book section: the Story + Style builder
     [data-mp-look]      Royal Look: sizes per piece, total, add the look
     [data-mp-sticky-atc] product pages: sticky add-to-bag bar

   Pointer effects only run with a real mouse or trackpad, and nothing moves
   for visitors who have asked their device for reduced motion. Every section
   can be rendered more than once on a page (and re-rendered by the theme
   editor), so each element is set up at most once and init() can run again
   on any part of the page.
   ========================================================================== */
(function () {
  'use strict';

  // Each section links this file, so it can be loaded several times.
  if (window.MPDepth) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function canMove() {
    return finePointer.matches && !reduce.matches;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function setupTilt(el) {
    if (el.__mpTilt) return;
    el.__mpTilt = true;

    var max = parseFloat(el.getAttribute('data-mp-tilt')) || 7;
    var frame = 0;

    el.addEventListener('pointermove', function (e) {
      if (!canMove()) return;
      var r = el.getBoundingClientRect();
      var x = (e.clientX - r.left) / r.width;
      var y = (e.clientY - r.top) / r.height;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        el.classList.add('is-tilting');
        el.style.setProperty('--rx', ((0.5 - y) * max).toFixed(2) + 'deg');
        el.style.setProperty('--ry', ((x - 0.5) * max).toFixed(2) + 'deg');
        el.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
        el.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
      });
    });

    el.addEventListener('pointerleave', function () {
      cancelAnimationFrame(frame);
      el.classList.remove('is-tilting');
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    });
  }

  function setupParallax(el) {
    if (el.__mpParallax) return;
    el.__mpParallax = true;

    var frame = 0;
    var visible = true;

    el.addEventListener('pointermove', function (e) {
      if (!canMove()) return;
      var r = el.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 2 - 1;
      var y = ((e.clientY - r.top) / r.height) * 2 - 1;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        el.style.setProperty('--px', x.toFixed(3));
        el.style.setProperty('--py', y.toFixed(3));
      });
    });

    el.addEventListener('pointerleave', function () {
      el.style.setProperty('--px', '0');
      el.style.setProperty('--py', '0');
    });

    // Scroll depth is cheap enough for phones too, but only while in view.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }).observe(el);
    }

    var ticking = false;
    function onScroll() {
      if (!visible || reduce.matches || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var r = el.getBoundingClientRect();
        var progress = Math.min(Math.max(-r.top / r.height, 0), 1);
        el.style.setProperty('--sy', progress.toFixed(3));
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Hero slider. Slides cross-fade (CSS); this moves the active class, keeps
     hidden slides inert, plays only the visible video, and runs autoplay.
     Autoplay pauses on hover, on keyboard focus and in a background tab, and
     never runs for reduced-motion visitors. */
  function setupSlider(root) {
    if (root.__mpSlider) return;
    root.__mpSlider = true;

    var slides = root.querySelectorAll('.mph__slide');
    var dots = root.querySelectorAll('[data-mp-dot]');
    var count = slides.length;
    if (count < 2) {
      // A single slide still needs its (visible) video started.
      var only = slides[0] && slides[0].querySelectorAll('video');
      if (only && !reduce.matches) {
        each(only, function (v) {
          if (getComputedStyle(v).display === 'none') return;
          v.muted = true;
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        });
      }
      return;
    }

    var current = 0;
    var timer = 0;
    var hovered = false;
    var focused = false;
    var seconds = parseFloat(root.getAttribute('data-interval')) || 6;
    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduce.matches;
    if (autoplay) root.classList.add('is-autoplay');

    // The visible video of a slide (a slide may hold a desktop and a phone one).
    function videoOf(slide) {
      var vids = slide.querySelectorAll('video');
      for (var i = 0; i < vids.length; i++) {
        if (vids[i].offsetParent !== null || getComputedStyle(vids[i]).display !== 'none') return vids[i];
      }
      return vids[0] || null;
    }

    function pauseVideos(slide) {
      each(slide.querySelectorAll('video'), function (v) { v.pause(); });
    }

    // Seconds a slide stays up: a video slide holds for its whole video.
    function holdFor(slide) {
      var h = parseFloat(slide.getAttribute('data-hold'));
      return h > seconds ? h : seconds;
    }

    // If the browser refuses to start (not loaded yet, tab still in the
    // background), try again once the video can play.
    function playVideo(slide) {
      var v = videoOf(slide);
      if (!v) return;
      v.muted = true;
      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          v.addEventListener('canplay', function () {
            if (slides[current] === slide && v.paused) {
              var q = v.play();
              if (q && q.catch) q.catch(function () {});
            }
          }, { once: true });
        });
      }
    }

    function restartProgress(dot) {
      var bar = dot.querySelector('span');
      if (!bar) return;
      dot.classList.remove('is-active');
      void bar.offsetWidth; // restart the CSS fill animation
      dot.classList.add('is-active');
    }

    function schedule() {
      clearTimeout(timer);
      var hold = holdFor(slides[current]);
      root.style.setProperty('--mph-hold', hold + 's');
      if (!autoplay || hovered || focused || document.hidden) return;
      timer = setTimeout(function () { go(current + 1); }, hold * 1000);
      // Keep the gold progress line in step with the timer after a pause.
      if (dots[current]) restartProgress(dots[current]);
    }

    function setPaused() {
      var paused = hovered || focused || document.hidden;
      // Coming back to the tab: make sure the visible video is running.
      if (!document.hidden && !reduce.matches) {
        var v = videoOf(slides[current]);
        if (v && v.paused) playVideo(slides[current]);
      }
      root.classList.toggle('is-paused', paused);
      schedule();
    }

    function go(index) {
      index = (index + count) % count;
      if (index === current) { schedule(); return; }

      var old = slides[current];
      var next = slides[index];
      old.classList.remove('is-active');
      old.setAttribute('inert', '');
      pauseVideos(old);

      next.removeAttribute('inert');
      next.classList.add('is-active');
      playVideo(next);

      each(dots, function (d, i) {
        if (i === index) {
          restartProgress(d);
          d.setAttribute('aria-current', 'true');
        } else {
          d.classList.remove('is-active');
          d.removeAttribute('aria-current');
        }
      });

      current = index;
      schedule();
    }

    // Only the first slide's video plays to begin with.
    each(slides, function (s, i) {
      if (i !== 0) pauseVideos(s);
    });
    if (!reduce.matches) playVideo(slides[0]);

    var prev = root.querySelector('[data-mp-prev]');
    var next = root.querySelector('[data-mp-next]');
    if (prev) prev.addEventListener('click', function () { go(current - 1); });
    if (next) next.addEventListener('click', function () { go(current + 1); });
    each(dots, function (d, i) {
      d.addEventListener('click', function () { go(i); });
    });

    root.addEventListener('mouseenter', function () { hovered = true; setPaused(); });
    root.addEventListener('mouseleave', function () { hovered = false; setPaused(); });
    root.addEventListener('focusin', function () { focused = true; setPaused(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { focused = false; setPaused(); }
    });
    document.addEventListener('visibilitychange', setPaused);

    // Swipe on touch screens.
    var startX = null;
    var startY = null;
    root.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      startX = e.clientX;
      startY = e.clientY;
    });
    root.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(current + (dx < 0 ? 1 : -1));
    });

    // Theme editor: show the slide being edited.
    root.addEventListener('shopify:block:select', function (e) {
      var i = Array.prototype.indexOf.call(slides, e.target);
      if (i > -1) { focused = true; go(i); setPaused(); }
    });
    root.addEventListener('shopify:block:deselect', function () { focused = false; setPaused(); });

    schedule();
  }

  function setupFlip(btn) {
    if (btn.__mpFlip) return;
    btn.__mpFlip = true;

    btn.addEventListener('click', function () {
      var card = btn.closest('.mpf__card');
      if (!card) return;
      var flipped = card.classList.toggle('is-flipped');
      btn.setAttribute('aria-pressed', flipped ? 'true' : 'false');
    });
  }

  function setupSwitcher(root) {
    if (root.__mpSwitcher) return;
    root.__mpSwitcher = true;

    var swatches = root.querySelectorAll('[data-mp-swatch]');
    var shots = root.querySelectorAll('[data-mp-shot]');
    var details = root.querySelectorAll('[data-mp-detail]');
    var current = 0;

    function show(index, focus) {
      if (index === current) return;

      each(shots, function (shot, i) {
        shot.classList.remove('is-leaving');
        if (i === current) shot.classList.add('is-leaving');
        shot.classList.toggle('is-active', i === index);
      });
      each(details, function (d, i) {
        d.hidden = i !== index;
      });
      each(swatches, function (s, i) {
        var on = i === index;
        s.setAttribute('aria-checked', on ? 'true' : 'false');
        s.tabIndex = on ? 0 : -1;
        if (on && focus) s.focus();
      });

      var wash = swatches[index].getAttribute('data-wash');
      if (wash) root.style.setProperty('--wash', wash);
      current = index;
      // Lets other behaviour (the count-up numbers) react to the new panel.
      root.dispatchEvent(new CustomEvent('mp:switch', { detail: { index: index } }));
    }

    each(swatches, function (s, i) {
      s.addEventListener('click', function () { show(i, false); restart(); });

      // Arrow keys move between colours, as in any radio group.
      s.addEventListener('keydown', function (e) {
        var step = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') step = 1;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') step = -1;
        if (!step) return;
        e.preventDefault();
        show((current + step + swatches.length) % swatches.length, true);
        restart();
      });
    });

    // Optional rotation (data-autoplay = seconds, 0 = off). Pauses while the
    // pointer or keyboard focus is inside, and never runs for reduced motion.
    var seconds = parseFloat(root.getAttribute('data-autoplay')) || 0;
    var timer = 0;
    var paused = false;
    function restart() {
      clearInterval(timer);
      if (!seconds || reduce.matches || paused || swatches.length < 2) return;
      timer = setInterval(function () {
        if (!document.hidden) show((current + 1) % swatches.length, false);
      }, seconds * 1000);
    }
    if (seconds) {
      root.addEventListener('mouseenter', function () { paused = true; restart(); });
      root.addEventListener('mouseleave', function () { paused = false; restart(); });
      root.addEventListener('focusin', function () { paused = true; restart(); });
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) { paused = false; restart(); }
      });
      restart();
    }
  }

  /* Count-up numbers: any [data-count] inside a [data-mp-counters] section
     counts from zero to its value ("100+", "1k", "25") the first time the
     section scrolls into view, and again whenever a switcher shows a new panel.
     The final text is in the HTML, so without the script it simply shows. */
  function animateCount(el) {
    var target = el.getAttribute('data-count') || '';
    var m = target.match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m || reduce.matches) { el.textContent = target; return; }
    var end = parseFloat(m[2].replace(/,/g, ''));
    var decimals = (m[2].split('.')[1] || '').length;
    var start = null;
    var duration = 1400;
    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      var value = (end * eased).toFixed(decimals);
      el.textContent = m[1] + (end >= 1000 ? Number(value).toLocaleString() : value) + m[3];
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function setupCounters(root) {
    if (root.__mpCounters) return;
    root.__mpCounters = true;
    var seen = false;

    function run() {
      each(root.querySelectorAll('[data-count]'), function (el) {
        if (el.closest('[hidden]')) return;
        animateCount(el);
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting && !seen) { seen = true; run(); io.disconnect(); }
      }, { threshold: 0.3 });
      io.observe(root);
    }
    root.addEventListener('mp:switch', function () { if (seen) run(); });
  }

  // Shopify money formats look like "${{amount}}" or "{{amount_with_comma_separator}} €".
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var thousands = function (n, sep) { return n.replace(/\B(?=(\d{3})+(?!\d))/g, sep); };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, kind) {
      if (kind === 'amount_no_decimals') return thousands(String(Math.round(cents / 100)), ',');
      if (kind === 'amount_with_comma_separator') return thousands(parts[0], '.') + ',' + parts[1];
      if (kind === 'amount_no_decimals_with_comma_separator') return thousands(String(Math.round(cents / 100)), '.');
      return thousands(parts[0], ',') + '.' + parts[1];
    });
  }

  /* Story + Style builder: pick one or more books, a set and a size. The
     chosen items are drawn together in [data-mp-preview], and one button adds
     them all to the cart, then goes to the cart page. The price shown takes
     the chosen set's data-saving (or the section default) off ONCE per
     bundle, which must match the automatic discounts in Shopify admin; this
     script never changes what the cart charges. */
  function setupBundle(root) {
    if (root.__mpBundle) return;
    root.__mpBundle = true;

    var setsEl = root.querySelector('[data-mp-sets]');
    var sizeSelect = root.querySelector('[data-mp-size]');
    var totalEl = root.querySelector('[data-mp-total]');
    var compareEl = root.querySelector('[data-mp-compare]');
    var addBtn = root.querySelector('[data-mp-add]');
    var errorEl = root.querySelector('[data-mp-error]');
    var shots = root.querySelectorAll('[data-mp-book-shot]');
    var preview = root.querySelector('[data-mp-preview]');
    if (!setsEl || !sizeSelect || !addBtn) return;

    var sets = {};
    try { sets = JSON.parse(setsEl.textContent); } catch (e) { return; }
    var saving = parseInt(root.getAttribute('data-saving'), 10) || 0;
    var moneyFormat = root.getAttribute('data-money-format');

    function checked(prefix) {
      return root.querySelector('input[name^="' + prefix + '"]:checked');
    }

    function checkedBooks() {
      return Array.prototype.slice.call(root.querySelectorAll('input[name^="mpb-book-"]:checked'));
    }

    // One picture-and-name tile in the preview row.
    function tile(img, name, price, was) {
      var item = document.createElement('div');
      item.className = 'mpb__pick';
      if (img) {
        var pic = document.createElement('img');
        pic.src = img;
        pic.alt = '';
        pic.width = 120;
        pic.height = 120;
        pic.loading = 'lazy';
        item.appendChild(pic);
      }
      var label = document.createElement('span');
      label.className = 'mpb__pick-name';
      label.textContent = name;
      item.appendChild(label);
      var cost = document.createElement('span');
      cost.className = 'mpb__pick-price';
      cost.textContent = price;
      if (was) {
        var s = document.createElement('s');
        s.textContent = was;
        cost.appendChild(document.createTextNode(' '));
        cost.appendChild(s);
      }
      item.appendChild(cost);
      return item;
    }

    function plus() {
      var p = document.createElement('span');
      p.className = 'mpb__plus';
      p.setAttribute('aria-hidden', 'true');
      p.textContent = '+';
      return p;
    }

    // The saving for the chosen set, taken off once per bundle.
    function savingFor(set) {
      var own = set ? parseInt(set.getAttribute('data-saving'), 10) : NaN;
      return isNaN(own) ? saving : own;
    }

    function drawPreview(books, set, size) {
      if (!preview) return;
      preview.innerHTML = '';
      if (!books.length || !set) return;
      books.forEach(function (b, i) {
        if (i > 0) preview.appendChild(plus());
        preview.appendChild(tile(
          b.getAttribute('data-img'),
          b.getAttribute('data-name'),
          formatMoney(parseInt(b.getAttribute('data-price'), 10), moneyFormat)
        ));
      });
      preview.appendChild(plus());
      var setName = set.getAttribute('data-name') + (size ? ' · ' + size.text : '');
      preview.appendChild(tile(
        set.getAttribute('data-img'),
        setName,
        size ? formatMoney(parseInt(size.getAttribute('data-price'), 10), moneyFormat) : ''
      ));
      var off = savingFor(set);
      if (off > 0) {
        var line = document.createElement('p');
        line.className = 'mpb__saving';
        line.textContent = 'Bundle saving −' + formatMoney(off, moneyFormat);
        preview.appendChild(line);
      }
    }

    function showBook(productId) {
      each(shots, function (shot) {
        var on = shot.getAttribute('data-mp-book-shot') === String(productId);
        if (shot.classList.contains('is-active') && !on) {
          shot.classList.add('is-leaving');
          setTimeout(function () { shot.classList.remove('is-leaving'); }, 700);
        }
        shot.classList.toggle('is-active', on);
      });
    }

    function fillSizes() {
      var set = checked('mpb-set-');
      var variants = set ? sets[set.value] || [] : [];
      var previous = sizeSelect.options[sizeSelect.selectedIndex];
      var previousTitle = previous ? previous.text.replace(/ — sold out$/, '') : '';
      sizeSelect.innerHTML = '';
      var picked = false;
      variants.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.text = v.title + (v.available ? '' : ' — sold out');
        opt.disabled = !v.available;
        opt.setAttribute('data-price', v.price);
        if (v.available && !picked && v.title === previousTitle) { opt.selected = true; picked = true; }
        sizeSelect.appendChild(opt);
      });
      if (!picked) {
        var firstOk = Array.prototype.find.call(sizeSelect.options, function (o) { return !o.disabled; });
        if (firstOk) firstOk.selected = true;
      }
    }

    function update() {
      var books = checkedBooks();
      var set = checked('mpb-set-');
      var size = sizeSelect.options[sizeSelect.selectedIndex];
      var ok = books.length > 0 && size && !size.disabled;
      addBtn.disabled = !ok;
      drawPreview(books, set, size && !size.disabled ? size : null);
      if (!ok) {
        totalEl.textContent = books.length ? '' : 'Choose at least one book';
        compareEl.textContent = '';
        return;
      }
      var full = parseInt(size.getAttribute('data-price'), 10);
      books.forEach(function (b) { full += parseInt(b.getAttribute('data-price'), 10); });
      var off = Math.min(savingFor(set), full);
      totalEl.textContent = formatMoney(full - off, moneyFormat);
      compareEl.textContent = off > 0 ? formatMoney(full, moneyFormat) : '';
    }

    root.addEventListener('change', function (e) {
      var name = e.target.name || '';
      if (name.indexOf('mpb-set-') === 0) fillSizes();
      if (name.indexOf('mpb-book-') === 0) {
        // Show the book just ticked; if one was unticked, show one still chosen.
        var shown = e.target.checked ? e.target : checkedBooks()[0];
        if (shown) showBook(shown.getAttribute('data-product'));
      }
      if (errorEl) errorEl.hidden = true;
      update();
    });

    // Hovering a book in the list previews it in the photo panel.
    each(root.querySelectorAll('[data-mp-book-preview]'), function (row) {
      var id = row.getAttribute('data-mp-book-preview');
      row.addEventListener('mouseenter', function () { showBook(id); });
      row.addEventListener('mouseleave', function () {
        var book = checkedBooks()[0];
        if (book) showBook(book.getAttribute('data-product'));
      });
    });

    addBtn.addEventListener('click', function () {
      var books = checkedBooks();
      var size = sizeSelect.options[sizeSelect.selectedIndex];
      if (!books.length || !size || size.disabled) return;

      var items = books.map(function (b) { return { id: parseInt(b.value, 10), quantity: 1 }; });
      items.push({ id: parseInt(size.value, 10), quantity: 1 });

      var label = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';

      fetch(root.getAttribute('data-cart-add-url') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag.');
            window.location.href = root.getAttribute('data-cart-url');
          });
        })
        .catch(function (err) {
          addBtn.disabled = false;
          addBtn.textContent = label;
          if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        });
    });

    fillSizes();
    update();
  }

  /* Royal Look: one outfit of the month. Each piece has a size/colour
     ([data-mp-variant]) and an include tick ([data-mp-include]); the total
     sums the ticked pieces and one button adds them all to the cart.
     data-prefer on the look ("Gold, Cream") pre-selects matching options. */
  function setupLook(look) {
    if (look.__mpLook) return;
    look.__mpLook = true;

    var section = look.closest('.mpl');
    var moneyFormat = section ? section.getAttribute('data-money-format') : null;
    var items = look.querySelectorAll('.mpl__item');
    var totalEl = look.querySelector('[data-mp-look-total]');
    var addBtn = look.querySelector('[data-mp-look-add]');
    var errorEl = look.querySelector('[data-mp-look-error]');
    var prefer = (look.getAttribute('data-prefer') || '')
      .split(',')
      .map(function (w) { return w.trim().toLowerCase(); })
      .filter(Boolean);

    function chosen(item) {
      var v = item.querySelector('[data-mp-variant]');
      if (!v) return null;
      if (v.tagName === 'SELECT') {
        var opt = v.options[v.selectedIndex];
        return opt && !opt.disabled ? { id: opt.value, price: parseInt(opt.getAttribute('data-price'), 10) } : null;
      }
      return { id: v.value, price: parseInt(v.getAttribute('data-price'), 10) };
    }

    // Pre-select the available option that best matches the preferred words.
    // Each part of an option ("Cream / Kids") scores 2 for an exact match
    // ("gold" = "Gold") and 1 for a partial one ("gold" in "Rose gold"), so
    // "Gold" beats "Rose gold" and "Cream / Kids" beats "Teal / Kids".
    if (prefer.length) {
      each(look.querySelectorAll('select[data-mp-variant]'), function (sel) {
        var best = null;
        var bestScore = 0;
        each(sel.options, function (o) {
          if (o.disabled) return;
          var score = 0;
          o.text.toLowerCase().split('/').forEach(function (part) {
            part = part.replace(/—.*$/, '').trim();
            prefer.forEach(function (w) {
              if (part === w) score += 2;
              else if (part.indexOf(w) > -1) score += 1;
            });
          });
          if (score > bestScore) { bestScore = score; best = o; }
        });
        if (best) best.selected = true;
      });
    }

    function update() {
      var total = 0;
      var count = 0;
      each(items, function (item) {
        var inc = item.querySelector('[data-mp-include]');
        var c = chosen(item);
        var priceEl = item.querySelector('[data-mp-line-price]');
        if (c && priceEl) priceEl.textContent = formatMoney(c.price, moneyFormat);
        item.classList.toggle('is-off', !(inc && inc.checked));
        if (inc && inc.checked && c) { total += c.price; count++; }
      });
      if (totalEl) totalEl.textContent = count ? formatMoney(total, moneyFormat) : '—';
      if (addBtn) addBtn.disabled = count === 0;
    }

    look.addEventListener('change', function () {
      if (errorEl) errorEl.hidden = true;
      update();
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var payload = [];
        each(items, function (item) {
          var inc = item.querySelector('[data-mp-include]');
          var c = chosen(item);
          if (inc && inc.checked && c) payload.push({ id: parseInt(c.id, 10), quantity: 1 });
        });
        if (!payload.length) return;
        var label = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        fetch(section.getAttribute('data-cart-add-url') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload })
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag.');
              window.location.href = section.getAttribute('data-cart-url');
            });
          })
          .catch(function (err) {
            addBtn.disabled = false;
            addBtn.textContent = label;
            if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
          });
      });
    }

    // Theme editor: selecting a look block shows that look.
    look.addEventListener('shopify:block:select', function () {
      each(section.querySelectorAll('[data-mp-look]'), function (l) { l.hidden = l !== look; });
    });

    update();
  }

  /* Sticky add-to-bag bar on product pages. Appears once the product form's
     own button ([data-prm-add]) has scrolled up out of view. "add" mode
     presses that button (so the theme's cart logic runs); "choose" mode
     scrolls back to the buying panel so a size can be picked first. */
  function setupStickyAtc(bar) {
    if (bar.__mpSticky) return;
    bar.__mpSticky = true;

    var realBtn = document.querySelector('[data-prm-add]');
    var btn = bar.querySelector('.mps-atc__btn');
    if (!realBtn || !btn) return;
    bar.hidden = false;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        // Show only when the real button is above the screen, not below it.
        var past = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', past);
      }).observe(realBtn);
    }

    btn.addEventListener('click', function () {
      if (bar.getAttribute('data-mode') === 'add' && !realBtn.disabled) {
        realBtn.click();
        return;
      }
      var panel = realBtn.closest('form') || realBtn;
      panel.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
    });
  }

  function init(scope) {
    scope = scope || document;
    each(scope.querySelectorAll('[data-mp-parallax]'), setupParallax);
    each(scope.querySelectorAll('[data-mp-slider]'), setupSlider);
    each(scope.querySelectorAll('[data-mp-tilt]'), setupTilt);
    each(scope.querySelectorAll('[data-mp-flip]'), setupFlip);
    each(scope.querySelectorAll('[data-mp-switcher]'), setupSwitcher);
    each(scope.querySelectorAll('[data-mp-counters]'), setupCounters);
    each(scope.querySelectorAll('[data-mp-bundle]'), setupBundle);
    each(scope.querySelectorAll('[data-mp-look]'), setupLook);
    each(scope.querySelectorAll('[data-mp-sticky-atc]'), setupStickyAtc);
  }

  window.MPDepth = { init: init };
  init(document);

  // Theme editor: a section that is edited is re-rendered and needs setting up again.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
